import { useEffect, useRef, useState } from 'react';
import Point from '@arcgis/core/geometry/Point';
import * as locator from '@arcgis/core/rest/locator';
import { Input } from '@/components/ui/input';
import { useUscgMapContext } from './UscgMapContext';
import { WGS84_SPATIAL_REFERENCE } from './utils/spatialReference';

const GEOCODE_URL =
  'https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer';

type AddressSuggestion = {
  text: string;
  magicKey: string;
};

type AddressLocationSearchProps = {
  onSelect: (location: { latitude: number; longitude: number }) => void;
};

export function AddressLocationSearch({
  onSelect,
}: AddressLocationSearchProps) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [status, setStatus] = useState<'idle' | 'searching' | 'empty'>('idle');
  const [menuOpen, setMenuOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const { mapView } = useUscgMapContext();

  const trimmedQuery = query.trim();
  const canSearch = trimmedQuery.length >= 3;

  useEffect(() => {
    if (!canSearch) return;

    let cancelled = false;
    const timeoutId = window.setTimeout(() => {
      if (cancelled) return;
      setStatus('searching');
      void locator
        .suggestLocations(GEOCODE_URL, {
          text: trimmedQuery,
          categories: ['Address', 'Postal', 'Populated Place'],
          location: new Point({
            longitude: mapView?.center?.longitude ?? -98.5,
            latitude: mapView?.center?.latitude ?? 39.8,
            spatialReference: WGS84_SPATIAL_REFERENCE,
          }),
        })
        .then((results) => {
          if (cancelled) return;
          const next = results
            .flatMap((result) => {
              if (!result.text || !result.magicKey) return [];
              return [{ text: result.text, magicKey: result.magicKey }];
            })
            .slice(0, 5);
          setSuggestions(next);
          setMenuOpen(next.length > 0);
          setStatus(next.length ? 'idle' : 'empty');
        })
        .catch(() => {
          if (cancelled) return;
          setSuggestions([]);
          setStatus('empty');
        });
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [canSearch, mapView, trimmedQuery]);

  const visibleSuggestions = canSearch ? suggestions : [];
  const visibleStatus = canSearch ? status : 'idle';

  const choose = async (suggestion: AddressSuggestion) => {
    setStatus('searching');
    try {
      const matches = await locator.addressToLocations(GEOCODE_URL, {
        address: { SingleLine: suggestion.text },
        magicKey: suggestion.magicKey,
        maxLocations: 1,
      });
      const location = matches[0]?.location;
      if (
        location?.latitude == null ||
        location.longitude == null ||
        Number.isNaN(location.latitude) ||
        Number.isNaN(location.longitude)
      ) {
        setStatus('empty');
        return;
      }
      setQuery(suggestion.text);
      setSuggestions([]);
      setMenuOpen(false);
      setStatus('idle');
      onSelect({
        latitude: location.latitude,
        longitude: location.longitude,
      });
    } catch {
      setStatus('empty');
    }
  };

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, []);

  return (
    <div ref={rootRef} className="tw:flex tw:flex-col tw:gap-1">
      <Input
        value={query}
        placeholder="Search address"
        aria-label="Search address"
        autoFocus
        onChange={(event) => {
          setQuery(event.target.value);
          setMenuOpen(true);
        }}
        onFocus={() => setMenuOpen(true)}
      />
      {visibleStatus === 'searching' ? (
        <p className="tw:mb-0 tw:text-xs tw:text-muted-foreground">
          Searching…
        </p>
      ) : null}
      {visibleStatus === 'empty' ? (
        <p className="tw:mb-0 tw:text-xs tw:text-muted-foreground">
          No addresses match.
        </p>
      ) : null}
      {menuOpen && visibleSuggestions.length > 0 ? (
        <ul className="tw:!m-0 tw:list-none tw:rounded-md tw:border tw:border-input tw:!p-0 tw:!pl-0">
          {visibleSuggestions.map((suggestion) => (
            <li key={suggestion.magicKey} className="tw:!pl-0">
              <button
                type="button"
                className="tw:w-full tw:px-3 tw:py-1.5 tw:text-left tw:text-xs tw:hover:bg-accent"
                onClick={() => void choose(suggestion)}
              >
                {suggestion.text}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
